import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SlidersHorizontal, Search, X } from "lucide-react";

const SORTS = [
  { v: "relevance", l: "Relevance" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
  { v: "possession", l: "Possession Date" },
  { v: "recent", l: "Recently Added" },
];
const ANY = "__any__";

export default function Properties() {
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState({ locations: [], types: [], builders: [], statuses: [], bhks: [] });
  const [projects, setProjects] = useState(null);
  const [f, setF] = useState({
    location: sp.get("location") || "", type: sp.get("type") || "", bhk: "", builder: "", status: "",
    min_budget: "", max_budget: "", rera_approved: false, search: "", sort: "relevance",
  });

  useEffect(() => { api.get("/projects/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  const fetchProjects = useCallback(async () => {
    setProjects(null);
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) params.set(k, v); });
    try {
      const { data } = await api.get(`/projects?${params.toString()}`);
      setProjects(data);
    } catch { setProjects([]); }
  }, [f]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v === ANY ? "" : v }));
  const reset = () => setF({ location: "", type: "", bhk: "", builder: "", status: "", min_budget: "", max_budget: "", rera_approved: false, search: "", sort: "relevance" });

  const FilterPanel = () => (
    <div className="space-y-5">
      <SelField label="Location" value={f.location} onChange={(v) => set("location", v)} options={filters.locations} testid="filter-location" />
      <SelField label="Property Type" value={f.type} onChange={(v) => set("type", v)} options={filters.types} testid="filter-type" />
      <SelField label="BHK Configuration" value={f.bhk} onChange={(v) => set("bhk", v)} options={filters.bhks} testid="filter-bhk" />
      <SelField label="Builder" value={f.builder} onChange={(v) => set("builder", v)} options={filters.builders} testid="filter-builder" />
      <SelField label="Construction Status" value={f.status} onChange={(v) => set("status", v)} options={filters.statuses} testid="filter-status" />
      <div>
        <Label className="text-xs font-semibold text-navy">Budget (₹)</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <Input placeholder="Min" type="number" value={f.min_budget} onChange={(e) => set("min_budget", e.target.value)} className="bg-gray-50" data-testid="filter-min-budget" />
          <Input placeholder="Max" type="number" value={f.max_budget} onChange={(e) => set("max_budget", e.target.value)} className="bg-gray-50" data-testid="filter-max-budget" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id="rera" checked={f.rera_approved} onCheckedChange={(v) => set("rera_approved", !!v)} data-testid="filter-rera" />
        <Label htmlFor="rera" className="text-sm text-navy cursor-pointer">RERA-approved only</Label>
      </div>
      <Button variant="ghost" onClick={reset} className="w-full text-gold-dark" data-testid="filter-reset"><X className="w-4 h-4 mr-1" /> Reset Filters</Button>
    </div>
  );

  return (
    <div>
      <SEO title="Properties for Sale in Bangalore | Mathura Homes"
        description="Browse verified apartments, villas and plots for sale in Bangalore. Filter by location, type, budget and construction status."
        jsonLd={{ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: "/" }, { "@type": "ListItem", position: 2, name: "Properties", item: "/properties" }] }} />

      <div className="bg-navy py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl md:text-5xl text-white">Properties in Bangalore</h1>
          <p className="text-gray-300 mt-3 max-w-2xl">Explore verified apartments, villas and plots. Use filters to find your perfect match.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-4 gap-8">
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-24 bg-white rounded-2xl border border-gray-100 shadow-card p-6">
            <h3 className="font-display text-xl text-navy mb-5 flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-gold" /> Filters</h3>
            <FilterPanel />
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by project, builder or location..." value={f.search} onChange={(e) => set("search", e.target.value)} className="pl-10 bg-white" data-testid="search-input" />
            </div>
            <div className="flex gap-3">
              <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden border-navy text-navy" data-testid="mobile-filter-btn"><SlidersHorizontal className="w-4 h-4 mr-1" /> Filters</Button>
                </SheetTrigger>
                <SheetContent side="left" className="overflow-y-auto">
                  <SheetHeader><SheetTitle className="font-display text-navy">Filters</SheetTitle></SheetHeader>
                  <div className="mt-6"><FilterPanel /></div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {projects === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-24 bg-muted rounded-2xl" data-testid="no-results">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl text-navy">No properties found</h3>
              <p className="text-muted-foreground mt-2">Try adjusting your filters or reset them.</p>
              <Button onClick={reset} className="mt-4 bg-gold text-navy hover:bg-gold-dark">Reset Filters</Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4" data-testid="results-count">{projects.length} propert{projects.length === 1 ? "y" : "ies"} found</p>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {projects.map((p) => <PropertyCard key={p.id} p={p} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SelField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-navy">{label}</Label>
      <Select value={value || "__any__"} onValueChange={onChange}>
        <SelectTrigger className="bg-gray-50 mt-1" data-testid={testid}><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">Any</SelectItem>
          {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
